import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";
import { useAuth } from "./AuthContext.tsx";
import type { UserOrgMembership } from "../api/client.ts";

const STORAGE_KEY = "scaleupxq_selected_org";

interface OrganizationContextValue {
  selectedOrgId: string | null;
  setSelectedOrgId: (id: string | null) => void;
  organizations: UserOrgMembership[];
  currentOrg: UserOrgMembership | null;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const organizations = user?.organizations ?? [];

  const [selectedOrgId, setSelectedOrgIdState] = useState<string | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    // Validate stored ID is still valid
    if (stored && organizations.some((o) => o.id === stored)) {
      return stored;
    }
    // Default to first org
    return organizations.length > 0 ? organizations[0].id : null;
  });

  const setSelectedOrgId = (id: string | null) => {
    setSelectedOrgIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const currentOrg = organizations.find((o) => o.id === selectedOrgId) ?? null;

  return (
    <OrganizationContext.Provider
      value={{ selectedOrgId, setSelectedOrgId, organizations, currentOrg }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const ctx = useContext(OrganizationContext);
  if (!ctx)
    throw new Error("useOrganization must be used within OrganizationProvider");
  return ctx;
}
